import { ClassifiedGallery } from '@/components/classified-gallery';
import { ClassifiedsInlineBannerCarousel } from '@/components/classifieds-inline-banner-carousel';
import { Badge } from '@/components/ui/badge';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from '@/components/ui/carousel';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useBannerTracking } from '@/hooks/use-banner-tracking';
import { RecaptchaProvider, useRecaptchaToken } from '@/hooks/use-recaptcha';
import FrontendLayout from '@/layouts/frontend-layout';
import { apiClient } from '@/lib/apiClient';
import { buildClassifiedAbsoluteUrl, buildClassifiedPath } from '@/lib/classified-url';
import { getPublicPriceLabel } from '@/lib/currency';
import { getMediaUrl } from '@/lib/media';
import { capitalize, formatFileSize, getFileTypeLabel } from '@/lib/utils';
import { home, login } from '@/routes';
import { BreadcrumbItem as BreadcrumbItemType, Classified, MediaItem } from '@/types';
import { Head, router, usePage } from '@inertiajs/react';
import { ArrowLeft, Download, FileText, Heart } from 'lucide-react';
import React, { FormEventHandler, useState } from 'react';

interface Banner {
    id: number;
    title: string;
    link_url: string | null;
    section: string;
    is_active: boolean;
    description: string | null;
    image_alt: string | null;
    desktop_image: MediaItem | null;
    mobile_image: MediaItem | null;
    views_count: number;
    clicks_count: number;
}

interface OptionalCategory {
    id: number;
    name: string;
    slug: string;
    icon: string;
    order: number;
    optionals: Array<{
        id: number;
        name: string;
        slug: string;
        order: number;
    }>;
}

interface ClassifiedDetailProps {
    classified: Classified;
    similarOffers: Classified[];
    similarOffersBanner: Banner | null;
    optionalCategories?: OptionalCategory[];
}

interface GalleryImage {
    src: string;
    alt: string;
}

function ClassifiedDetailPage({ classified, similarOffers, similarOffersBanner, optionalCategories = [] }: ClassifiedDetailProps) {
    const { auth, appUrl } = usePage().props as any;
    const { trackBannerView, handleBannerClick } = useBannerTracking();
    const [showFullPhone, setShowFullPhone] = useState(false);
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
    const [isFavorited, setIsFavorited] = useState(classified.is_favorited ?? false);
    const [favoriteStates, setFavoriteStates] = useState<Record<number, boolean>>({});

    // Phone verification modal state
    const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
    const [isWhatsAppFlow, setIsWhatsAppFlow] = useState(false); // Track if modal was opened from WhatsApp button
    const [phoneVerificationForm, setPhoneVerificationForm] = useState({
        name: '',
        email: '',
        phone: '',
        termsAccepted: false,
    });
    const [phoneVerificationErrors, setPhoneVerificationErrors] = useState<Record<string, string>>({});
    const [phoneVerificationProcessing, setPhoneVerificationProcessing] = useState(false);

    const executeRecaptcha = useRecaptchaToken();

    const [processing, setProcessing] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const hasOptionals = (() => {
        let selected: string[] = [];
        if (Array.isArray(classified.optionals)) {
            selected = classified.optionals;
        } else if (typeof classified.optionals === 'object' && classified.optionals !== null) {
            selected = Object.values(classified.optionals).flat() as string[];
        }
        return selected.length > 0;
    })();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: 'Olá, tenho interesse. Por favor, entre em contato.',
        consentAccepted: false,
    });

    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            phone: '',
            subject: '',
            message: 'Olá, tenho interesse. Por favor, entre em contato.',
            consentAccepted: false,
        });
        setErrors({});
    };

    // Check if a field should be visible based on classified categories
    const isFieldVisible = (fieldName: string): boolean => {
        if (!classified.categories || classified.categories.length === 0) {
            return true; // Show all fields if no categories
        }

        // Check if ANY of the categories hides this field
        return !classified.categories.some((cat) => {
            const hiddenFields = cat.hidden_fields || [];
            return Array.isArray(hiddenFields) && hiddenFields.includes(fieldName);
        });
    };

    const hiddenPhone = classified.contact_phone
        ? `${classified.contact_phone.charAt(0) + classified.contact_phone.charAt(1) + classified.contact_phone.charAt(2)}••-••••`
        : '';
    const categories = classified.categories || [];
    const whatsappNumber = classified.contact_phone ? classified.contact_phone.replace(/\D/g, '') : '5541992235670';
    const classifiedPath = buildClassifiedPath(classified.slug);

    // Build breadcrumb hierarchy intelligently
    const buildBreadcrumbHierarchy = () => {
        // Find all categories by their actual levels
        const level1Categories = categories.filter((c) => !c.parent_id);
        const level2Categories = categories.filter((c) => c.parent_id && !c.parent?.parent_id);
        const level3Categories = categories.filter((c) => c.parent_id && c.parent?.parent_id);

        // Strategy 1: If we have a level 3 category, use its complete hierarchy
        if (level3Categories.length > 0) {
            const level3Category = level3Categories[0];
            return {
                level1: level3Category.parent?.parent || null,
                level2: level3Category.parent || null,
                level3: level3Category,
            };
        }

        // Strategy 2: If we have level 2 categories, use the first one with its parent
        if (level2Categories.length > 0) {
            const level2Category = level2Categories[0];
            return {
                level1: level2Category.parent || null,
                level2: level2Category,
                level3: null,
            };
        }

        // Strategy 3: Fallback to level 1 categories only
        if (level1Categories.length > 0) {
            return {
                level1: level1Categories[0],
                level2: null,
                level3: null,
            };
        }

        // Strategy 4: Last resort - use any category
        return {
            level1: categories[0] || null,
            level2: null,
            level3: null,
        };
    };

    const { level1: level1Category, level2: level2Category, level3: level3Category } = buildBreadcrumbHierarchy();

    const buildCategoryPath = (slugs: Array<string | undefined | null>) => {
        const validSlugs = slugs.filter((slug): slug is string => Boolean(slug));
        return validSlugs.length > 0 ? `/${validSlugs.join('/')}` : null;
    };

    const level1CategoryPath = buildCategoryPath([level1Category?.slug]);
    const level2CategoryPath = buildCategoryPath([level1Category?.slug, level2Category?.slug]);
    const level3CategoryPath = buildCategoryPath([level1Category?.slug, level2Category?.slug, level3Category?.slug]);

    const breadcrumbs: BreadcrumbItemType[] = [
        {
            title: 'Início',
            href: home().url,
        },
        ...(level1Category && level1CategoryPath
            ? [
                  {
                      title: level1Category.name,
                      href: level1CategoryPath,
                  } as BreadcrumbItemType,
              ]
            : []),
        ...(level2Category && level2CategoryPath
            ? [
                  {
                      title: level2Category.name,
                      href: level2CategoryPath,
                  } as BreadcrumbItemType,
              ]
            : []),
        ...(level3Category && level3CategoryPath
            ? [
                  {
                      title: level3Category.name,
                      href: level3CategoryPath,
                  } as BreadcrumbItemType,
              ]
            : []),
    ];

    const galleryImages: GalleryImage[] =
        classified.gallery && classified.gallery.length > 0
            ? classified.gallery.map((media, index) => ({
                  src: getMediaUrl(media, 'display', media.original_url),
                  alt: media.name || `${classified.title} - Image ${index + 1}`,
              }))
            : [];

    const handleImageClick = (index: number) => {
        setCurrentImageIndex(index);
        setIsGalleryOpen(true);
    };

    const handleGalleryClose = () => {
        setIsGalleryOpen(false);
    };

    const handleGalleryNavigate = (index: number) => {
        setCurrentImageIndex(index);
    };

    // Auto-favorite support after login: if user was redirected back with an auto_favorite flag,
    // and the classified is not yet favorited, toggle it once and then clean the URL.
    React.useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!auth?.user) return;
        if (isFavorited) return;

        try {
            const url = new URL(window.location.href);
            const autoFavorite = url.searchParams.get('auto_favorite');

            if (autoFavorite === '1') {
                apiClient
                    .post<{ is_favorited: boolean }>(`/api/favorites/${classified.id}/toggle`)
                    .then((response) => {
                        setIsFavorited(response.data.is_favorited);
                    })
                    .catch((error: unknown) => {
                        console.error('Error auto-toggling favorite after login:', error);
                    })
                    .finally(() => {
                        url.searchParams.delete('auto_favorite');
                        window.history.replaceState(null, '', url.toString());
                    });
            }
        } catch (error) {
            console.error('Error handling auto_favorite param:', error);
        }
    }, [auth?.user, isFavorited, classified.id]);

    // Sync mobile image counter with carousel navigation
    React.useEffect(() => {
        if (!carouselApi) return;
        const updateIndex = () => setCurrentImageIndex(carouselApi.selectedScrollSnap());
        updateIndex();
        carouselApi.on('select', updateIndex);
        carouselApi.on('reInit', updateIndex);
        return () => {
            carouselApi.off('select', updateIndex);
            carouselApi.off('reInit', updateIndex);
        };
    }, [carouselApi]);

    const handleFavorite = async (classifiedId: number) => {
        // Check if user is logged in
        if (!auth?.user) {
            // Redirect to login page with return URL, tagging it so we can auto-favorite after login
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set('auto_favorite', '1');

            router.visit(login().url, {
                data: { return_url: currentUrl.toString() },
            });
            return;
        }

        try {
            const response = await apiClient.post<{ is_favorited: boolean }>(`/api/favorites/${classifiedId}/toggle`);

            // Update local state
            if (classifiedId === classified.id) {
                setIsFavorited(response.data.is_favorited);
            } else {
                setFavoriteStates((prev) => ({
                    ...prev,
                    [classifiedId]: response.data.is_favorited,
                }));
            }
        } catch (error: unknown) {
            const response =
                typeof error === 'object' && error !== null && 'response' in error
                    ? (error as { response?: { status?: number } }).response
                    : undefined;

            if (response?.status === 401) {
                // User is not authenticated, redirect to login with return URL
                router.visit(login().url, {
                    data: { return_url: window.location.href },
                });
            } else {
                console.error('Error toggling favorite:', error);
            }
        }
    };

    const handleSubmit: FormEventHandler = async (e) => {
        e.preventDefault();
        setSubmitMessage(null);
        setErrors({});

        // Validate email format
        if (formData.email && !validateEmail(formData.email)) {
            setErrors({ email: 'Por favor, insira um email válido' });
            setSubmitMessage({ type: 'error', text: 'Por favor, insira um email válido.' });
            return;
        }

        if (!formData.consentAccepted) {
            setErrors({ consentAccepted: 'Você deve aceitar os termos para enviar a mensagem.' });
            setSubmitMessage({ type: 'error', text: 'Você deve aceitar os termos para enviar a mensagem.' });
            return;
        }

        setProcessing(true);

        try {
            let token = '';
            if (executeRecaptcha) {
                token = await executeRecaptcha('contact_form');
            }

            const response = await apiClient.post(`${classifiedPath}/contact`, {
                ...formData,
                recaptcha_token: token,
            });

            if (response.data.success) {
                resetForm();
                setSubmitMessage({
                    type: 'success',
                    text: 'Sua mensagem foi enviada com sucesso! O vendedor entrará em contato em breve.',
                });
            }
        } catch (error: any) {
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
                if (error.response.data.errors.recaptcha_token) {
                    setSubmitMessage({ type: 'error', text: 'Verificação reCAPTCHA falhou. Por favor, tente novamente.' });
                }
            } else {
                setSubmitMessage({ type: 'error', text: 'Houve um erro ao enviar sua mensagem. Tente novamente.' });
            }
        } finally {
            setProcessing(false);
        }
    };

    const handleClickPhone = () => {
        if (showFullPhone) {
            setShowFullPhone(false);
        } else {
            apiClient.post(`${classifiedPath}/click-phone`, {});
            setIsPhoneModalOpen(true);
        }
    };

    const handleScrollToContact = () => {
        const el = document.getElementById('contact-form');
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // Phone mask formatting function
    const formatPhoneMask = (value: string): string => {
        // Remove all non-numeric characters
        const numbers = value.replace(/\D/g, '');

        // Limit to 11 digits (DDD + 9 digits for mobile)
        const limited = numbers.slice(0, 11);

        if (limited.length === 0) return '';

        // Apply mask based on length
        if (limited.length <= 2) {
            return `(${limited}`;
        } else if (limited.length <= 6) {
            return `(${limited.slice(0, 2)}) ${limited.slice(2)}`;
        } else if (limited.length <= 10) {
            // Fixed phone: (XX) XXXX-XXXX
            return `(${limited.slice(0, 2)}) ${limited.slice(2, 6)}-${limited.slice(6)}`;
        } else {
            // Mobile phone: (XX) 9XXXX-XXXX
            return `(${limited.slice(0, 2)}) ${limited.slice(2, 7)}-${limited.slice(7, 11)}`;
        }
    };

    // Email validation function
    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handlePhoneVerification: FormEventHandler = async (e) => {
        e.preventDefault();

        setPhoneVerificationErrors({});

        // Validate name
        if (!phoneVerificationForm.name.trim()) {
            setPhoneVerificationErrors({ name: 'Por favor, insira seu nome' });
            return;
        }

        // Validate email
        if (!phoneVerificationForm.email || !validateEmail(phoneVerificationForm.email)) {
            setPhoneVerificationErrors({ email: 'Por favor, insira um email válido' });
            return;
        }

        // Validate phone (must have at least 10 digits)
        const cleanPhone = phoneVerificationForm.phone.replace(/\D/g, '');
        if (!phoneVerificationForm.phone || cleanPhone.length < 10) {
            setPhoneVerificationErrors({ phone: 'Por favor, insira um telefone válido (10 ou 11 dígitos)' });
            return;
        }

        if (!phoneVerificationForm.termsAccepted) {
            setPhoneVerificationErrors({ termsAccepted: 'Você precisa aceitar os termos para continuar.' });
            return;
        }

        setPhoneVerificationProcessing(true);

        try {
            let token = '';
            if (executeRecaptcha) {
                token = await executeRecaptcha('phone_verification');
            }

            try {
                // Send clean phone number (digits only) to backend
                const cleanPhoneForRequest = phoneVerificationForm.phone.replace(/\D/g, '');
                const requestData = {
                    name: phoneVerificationForm.name,
                    email: phoneVerificationForm.email,
                    phone: cleanPhoneForRequest,
                    recaptcha_token: token,
                    source: isWhatsAppFlow ? 'whatsapp' : 'view_phone',
                };

                const response = await apiClient.post(`${classifiedPath}/verify-phone`, requestData);

                if (response.data?.success) {
                    // Close modal and show phone number
                    setIsPhoneModalOpen(false);
                    setShowFullPhone(true);
                    // Reset form
                    setPhoneVerificationForm({ name: '', email: '', phone: '', termsAccepted: false });

                    // If opened from WhatsApp button, redirect to WhatsApp
                    if (isWhatsAppFlow) {
                        const whatsappUrl = `https://wa.me/${whatsappNumber}`;
                        window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
                        setIsWhatsAppFlow(false); // Reset flag
                    }
                    return;
                }
            } catch (error: any) {
                // If backend route is not available yet, gracefully proceed
                if (error?.response?.status === 404) {
                    setIsPhoneModalOpen(false);
                    setShowFullPhone(true);

                    // If opened from WhatsApp button, redirect to WhatsApp
                    if (isWhatsAppFlow) {
                        const whatsappUrl = `https://wa.me/${whatsappNumber}`;
                        window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
                        setIsWhatsAppFlow(false); // Reset flag
                    }
                    setPhoneVerificationForm({ name: '', email: '', phone: '', termsAccepted: false });
                    return;
                }
                // Propagate other errors to outer catch
                throw error;
            }
        } catch (error: any) {
            console.error('Phone verification error:', error);

            setPhoneVerificationErrors({});

            if (error?.response?.status === 422) {
                // Validation errors - Laravel returns errors as arrays
                const validationErrors: Record<string, string> = {};
                const errors = error.response.data?.errors || {};

                // Convert array errors to single string
                Object.keys(errors).forEach((key) => {
                    const errorMessages = errors[key];
                    validationErrors[key] = Array.isArray(errorMessages) ? errorMessages[0] : errorMessages;
                });

                setPhoneVerificationErrors(validationErrors);
            } else if (error?.response?.data?.errors) {
                // Other error format
                const errors: Record<string, string> = {};
                Object.keys(error.response.data.errors).forEach((key) => {
                    const errorMessages = error.response.data.errors[key];
                    errors[key] = Array.isArray(errorMessages) ? errorMessages[0] : errorMessages;
                });
                setPhoneVerificationErrors(errors);
            } else if (error?.response?.data?.message) {
                setPhoneVerificationErrors({ general: error.response.data.message });
            } else {
                setPhoneVerificationErrors({ general: 'Houve um erro ao verificar. Tente novamente.' });
            }
        } finally {
            setPhoneVerificationProcessing(false);
        }
    };

    // SEO data preparation
    const canonicalUrl = classified.public_url || buildClassifiedAbsoluteUrl(appUrl, classified.slug);
    const seoTitle = classified.seo_title || classified.title;
    const seoDescription = classified.seo_description || classified.summary || classified.description?.substring(0, 160);

    // Extract root category (level 1), manufacturer (level 2), model (level 3) from hierarchy
    const rootCategory = level1Category?.name || 'Embarcações';
    const manufacturerCategory = level2Category?.name || classified.manual_manufacturer;
    const modelCategory = level3Category?.name || classified.manual_model || classified.model;

    // Build page title: {title} à venda | {root_category}
    const pageTitle = `${seoTitle} à venda | ${rootCategory}`;

    // Build Product schema
    const productSchema = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: classified.title,
        url: canonicalUrl,
        image: classified.main_image ? [getMediaUrl(classified.main_image, 'social', getMediaUrl(classified.main_image, 'display'))] : [],
        ...(manufacturerCategory && {
            brand: {
                '@type': 'Brand',
                name: manufacturerCategory,
            },
        }),
        ...(modelCategory && { model: modelCategory }),
        category: rootCategory,
        ...(classified.price && {
            offers: {
                '@type': 'Offer',
                price: classified.price.toString(),
                priceCurrency: 'BRL',
                availability: 'https://schema.org/InStock',
                url: canonicalUrl,
            },
        }),
    };

    // Build BreadcrumbList schema
    const breadcrumbListItems = [
        {
            '@type': 'ListItem',
            position: 1,
            name: 'Início',
            item: appUrl,
        },
    ];

    let position = 2;
    if (level1Category && level1CategoryPath) {
        breadcrumbListItems.push({
            '@type': 'ListItem',
            position: position++,
            name: level1Category.name,
            item: `${appUrl}${level1CategoryPath}`,
        });
    }
    if (level2Category && level2CategoryPath) {
        breadcrumbListItems.push({
            '@type': 'ListItem',
            position: position++,
            name: level2Category.name,
            item: `${appUrl}${level2CategoryPath}`,
        });
    }
    if (level3Category && level3CategoryPath) {
        breadcrumbListItems.push({
            '@type': 'ListItem',
            position: position++,
            name: level3Category.name,
            item: `${appUrl}${level3CategoryPath}`,
        });
    }
    breadcrumbListItems.push({
        '@type': 'ListItem',
        position: position,
        name: classified.title,
        item: canonicalUrl,
    });

    const breadcrumbListSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbListItems,
    };

    return (
        <FrontendLayout>
            <Head title={pageTitle}>
                <meta name="description" content={seoDescription} />
                <link rel="canonical" href={canonicalUrl} />
                <meta name="robots" content="index,follow" />

                {/* Open Graph */}
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={seoDescription} />
                {classified.main_image && <meta property="og:image" content={getMediaUrl(classified.main_image, 'social')} />}
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:type" content="product" />
                <meta property="og:locale" content="pt_BR" />

                {/* JSON-LD schemas */}
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }} />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbListSchema) }} />

                {/* Fonts */}
                <link rel="preconnect" href="https://fonts.bunny.net" />
                <link href="https://fonts.bunny.net/css?family=instrument-sans:400,500,600" rel="stylesheet" />
            </Head>
            <div className="min-h-screen w-full bg-background pb-24 md:bg-gray-100">
                {galleryImages.length > 0 ? (
                    <section>
                        <div className="relative md:static">
                            <Carousel
                                opts={{
                                    align: 'start',
                                    loop: true,
                                }}
                                className="h-[297px] w-full md:h-[35vh]"
                                setApi={setCarouselApi}
                            >
                                <CarouselContent containerClassName="h-full" className="-ml-2 h-full md:-ml-4">
                                    {galleryImages.map((item, index) => (
                                        <CarouselItem key={index} className="h-full pl-2 md:basis-1/2 md:pl-4 lg:basis-1/3">
                                            <Card className="h-full cursor-pointer overflow-hidden p-0" onClick={() => handleImageClick(index)}>
                                                <div className="relative aspect-video h-full">
                                                    <img
                                                        src={item.src}
                                                        alt={item.alt}
                                                        className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                                                    />
                                                </div>
                                            </Card>
                                        </CarouselItem>
                                    ))}
                                </CarouselContent>
                                {/* Mobile-only nav buttons styled as circular white over image */}
                                <CarouselPrevious className="absolute top-auto bottom-2 left-2 h-8 w-8 translate-y-0 rounded-full bg-white text-black shadow md:top-1/2 md:bottom-auto md:-left-12 md:-translate-y-1/2" />
                                <CarouselNext className="absolute top-auto right-2 bottom-2 h-8 w-8 translate-y-0 rounded-full bg-white text-black shadow md:top-1/2 md:-right-12 md:bottom-auto md:-translate-y-1/2" />
                            </Carousel>

                            {/* Mobile-only overlay: image count and favorite */}
                            <div className="pointer-events-none absolute top-2 left-2 z-10 rounded-md bg-white/90 px-2 py-1 text-xs font-medium text-black md:hidden">
                                {currentImageIndex + 1}/{galleryImages.length}
                            </div>
                            {/* Mobile-only favorite button in slider */}
                            <Button
                                variant="ghost"
                                size="sm"
                                className="pointer-events-auto absolute top-2 right-2 z-10 size-8 rounded-full bg-white p-0 text-black shadow md:hidden"
                                onClick={() => handleFavorite(classified.id)}
                                aria-label={isFavorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                            >
                                <Heart className={`h-4 w-4 ${isFavorited ? 'fill-red-500 stroke-red-500' : ''}`} />
                            </Button>
                        </div>
                    </section>
                ) : (
                    <></>
                )}
                <section className={`px-4 sm:px-6 lg:px-12 ${galleryImages.length > 0 ? 'mt-4 md:-mt-8' : 'mt-8'} relative z-10`}>
                    <div className="grid auto-rows-max grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-[2fr_1fr] lg:gap-8">
                        <div className="col-span-1 rounded-none bg-transparent md:rounded-xl md:bg-white lg:col-span-1">
                            <div className="grid w-full grid-cols-1 grid-rows-[auto_auto_1fr_auto] gap-4">
                                {/* Title Section */}
                                <div className="row-start-1 px-4 py-3 sm:px-8 sm:py-4 md:border-b-2 md:border-solid md:border-b-gray-700 lg:px-12">
                                    <div className="flex items-end justify-between gap-4 md:items-start">
                                        <div className="min-w-0 flex-1 md:flex-none">
                                            <h1 className="text-2xl font-bold uppercase sm:text-3xl">{classified.title}</h1>
                                        </div>
                                        {/* Mobile-only price beside title */}
                                        {(classified.price || classified.price_on_request) && (
                                            <div className="flex-shrink-0 md:hidden">
                                                <div className="text-right text-base font-bold text-black uppercase">
                                                    {getPublicPriceLabel({
                                                        price: classified.price,
                                                        priceOnRequest: classified.price_on_request,
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="hidden shrink-0 bg-white hover:bg-gray-100 md:inline-flex"
                                            onClick={() => handleFavorite(classified.id)}
                                        >
                                            <Heart className={`h-5 w-5 ${isFavorited ? 'fill-red-500 stroke-red-500' : ''}`} />
                                        </Button>
                                    </div>
                                </div>
                                {/* Buttons, tags an Breadcrumbs */}
                                <div className="row-start-2 block space-y-3 px-4 pt-3 pb-0 sm:space-y-4 sm:px-8 sm:pt-4 sm:pb-0 md:py-4 lg:px-12">
                                    <div className="hidden flex-col items-start gap-2 sm:flex-row sm:gap-4 md:flex">
                                        <Button
                                            className="h-10 w-full rounded-lg bg-blue-800 px-4 text-xs font-medium hover:bg-blue-800 sm:h-12 sm:w-auto sm:px-8 sm:text-sm"
                                            asChild
                                        >
                                            <a
                                                href="https://page.comprenautica.com.br/seguros?utm_source=CompreNautica&utm_medium=site&utm_campaign=button-cta-classified"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Cotar Seguro
                                            </a>
                                        </Button>
                                        <Button
                                            className="h-10 w-full rounded-lg bg-blue-800 px-4 text-xs font-medium hover:bg-blue-800 sm:h-12 sm:w-auto sm:px-8 sm:text-sm"
                                            asChild
                                        >
                                            <a
                                                href="https://page.comprenautica.com.br/consorciocomprenautica?utm_source=CompreNautica&utm_medium=site&utm_campaign=button-cta-classified"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Cotar Consórcio
                                            </a>
                                        </Button>
                                        <Button className="h-10 w-full rounded-lg bg-blue-800 px-4 text-xs font-medium hover:bg-blue-800 sm:h-12 sm:w-auto sm:px-8 sm:text-sm">
                                            Resumo Inteligente{' '}
                                            <svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path
                                                    d="M12.5292 12.6128C12.3025 12.6128 12.1005 12.4698 12.0253 12.256L11.8375 11.7225C11.6498 11.1891 11.1458 10.8322 10.5803 10.8322H9.28128C8.71576 10.8322 8.21181 11.1891 8.02406 11.7225L7.8363 12.256C7.76104 12.4698 7.55905 12.6128 7.33238 12.6128C6.95993 12.6128 6.70178 12.2413 6.83171 11.8922L9.11408 5.76092C9.24107 5.41976 9.56675 5.19348 9.93078 5.19348C10.2948 5.19348 10.6205 5.41976 10.7475 5.76092L13.0298 11.8922C13.1598 12.2413 12.9016 12.6128 12.5292 12.6128ZM9.09563 8.67924C8.89161 9.2537 9.31763 9.85708 9.92724 9.85708C10.5359 9.85708 10.9618 9.25553 10.7596 8.68147C10.4833 7.89673 9.37405 7.89524 9.09563 8.67924Z"
                                                    fill="white"
                                                />
                                                <path
                                                    d="M14.2523 5.7246C14.2523 5.43127 14.4901 5.19348 14.7834 5.19348C15.0767 5.19348 15.3145 5.43127 15.3145 5.7246V12.0817C15.3145 12.375 15.0767 12.6128 14.7834 12.6128C14.4901 12.6128 14.2523 12.375 14.2523 12.0817V5.7246Z"
                                                    fill="white"
                                                />
                                                <path
                                                    d="M19.2158 0H3.39103C2.49167 0 1.62915 0.363476 0.993209 1.01047C0.357268 1.65746 0 2.53497 0 3.44995V14.9498C0 15.8648 0.357268 16.7423 0.993209 17.3893C1.62915 18.0363 2.49167 18.3997 3.39103 18.3997H12.2911C14.9778 18.3997 17.5515 19.4809 19.4322 21.3995L20.674 22.6662C20.7796 22.7728 20.9048 22.8571 21.0426 22.9143C21.1803 22.9715 21.3277 23.0006 21.4765 22.9997C21.6248 23.0036 21.7719 22.9721 21.906 22.9077C22.1125 22.8214 22.2892 22.6749 22.4139 22.4866C22.5386 22.2984 22.6057 22.0767 22.6068 21.8497V3.44995C22.6068 2.53497 22.2496 1.65746 21.6136 1.01047C20.9777 0.363476 20.1152 0 19.2158 0ZM20.3462 15.3774C20.3462 16.7302 18.7039 17.4001 17.7577 16.4333C17.6521 16.3267 17.5268 16.2424 17.3891 16.1851C17.2514 16.1279 17.1039 16.0989 16.9551 16.0998H3.39103C3.09124 16.0998 2.80374 15.9786 2.59175 15.763C2.37977 15.5473 2.26068 15.2548 2.26068 14.9498V3.44995C2.26068 3.14496 2.37977 2.85245 2.59175 2.63679C2.80374 2.42113 3.09124 2.29997 3.39103 2.29997H19.2158C19.5156 2.29997 19.8031 2.42113 20.0151 2.63679C20.2271 2.85245 20.3462 3.14496 20.3462 3.44995V15.3774Z"
                                                    fill="white"
                                                />
                                            </svg>
                                        </Button>
                                    </div>
                                    <div className="flex flex-row flex-wrap items-start gap-2 sm:gap-4">
                                        <Badge className="border border-blue-800 bg-white text-xs font-light text-black">{classified.year}</Badge>
                                        <Badge className="border border-blue-800 bg-white text-xs font-light text-black">
                                            {classified.custom_id ? `ID-${classified.custom_id}` : `ID-${classified.id}`}
                                        </Badge>
                                        <Badge className="border border-blue-800 bg-white text-xs font-light text-black">
                                            {classified.condition === 'new'
                                                ? 'Novo'
                                                : classified.condition === 'used'
                                                  ? 'Usado'
                                                  : classified.condition === 'used_excellent'
                                                    ? 'Usado, excelente estado'
                                                    : classified.condition === 'used_with_marks'
                                                      ? 'Usado com marcas'
                                                      : capitalize(classified.condition)}
                                        </Badge>
                                    </div>
                                    <div>
                                        <Breadcrumb>
                                            <BreadcrumbList>
                                                {breadcrumbs.map((item, index) => (
                                                    <React.Fragment key={item.href || index}>
                                                        <BreadcrumbItem>
                                                            <BreadcrumbLink href={item.href}>{item.title}</BreadcrumbLink>
                                                        </BreadcrumbItem>
                                                        {index < breadcrumbs.length - 1 ? <BreadcrumbSeparator /> : null}
                                                    </React.Fragment>
                                                ))}
                                            </BreadcrumbList>
                                        </Breadcrumb>
                                    </div>
                                </div>
                                {/* Information */}
                                <div className="row-start-3 px-4 pt-0 pb-3 sm:px-8 sm:pt-0 sm:pb-4 md:py-4 lg:px-12">
                                    <div className="grid h-full grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
                                        {[
                                            // Row 1: Cidade Estilo Tamanho
                                            { id: 1, label: 'Cidade', value: classified.city?.name || '--', fieldName: null },
                                            { id: 5, label: 'Estilo', value: classified.style || '--', fieldName: 'style' },
                                            {
                                                id: 6,
                                                label: 'Tamanho (pés)',
                                                value: classified.size ? `${classified.size} pés` : '--',
                                                fieldName: 'size',
                                            },
                                            // Row 2: Ano Passageiros Passageiros Noite Cabines Cabines Tripulação
                                            { id: 2, label: 'Ano', value: classified.year || '--', fieldName: null },
                                            { id: 7, label: 'Passageiros', value: classified.passengers || '--', fieldName: 'passengers' },
                                            {
                                                id: 14,
                                                label: 'Passageiros Noite',
                                                value: classified.overnight_passengers != null ? classified.overnight_passengers : '--',
                                                fieldName: 'overnight_passengers',
                                            },
                                            { id: 8, label: 'Cabines', value: classified.cabins || '--', fieldName: 'cabins' },
                                            { id: 15, label: 'Cabines Tripulação', value: classified.crew_cabins || '--', fieldName: 'crew_cabins' },
                                            // Row 3: Valor Camas Condições (Preço hidden on mobile, shown on desktop)
                                            {
                                                id: 3,
                                                label: 'Preço',
                                                value: getPublicPriceLabel({
                                                    price: classified.price,
                                                    priceOnRequest: classified.price_on_request,
                                                }),
                                                hideOnMobile: true,
                                                fieldName: null,
                                            },
                                            { id: 9, label: 'Camas', value: classified.beds || '--', fieldName: 'beds' },
                                            {
                                                id: 10,
                                                label: 'Condições',
                                                value:
                                                    classified.condition === 'new'
                                                        ? 'Novo'
                                                        : classified.condition === 'used'
                                                          ? 'Usado'
                                                          : classified.condition === 'used_excellent'
                                                            ? 'Usado, excelente estado'
                                                            : classified.condition === 'used_with_marks'
                                                              ? 'Usado com marcas'
                                                              : classified.condition
                                                                ? capitalize(classified.condition)
                                                                : '--',
                                                fieldName: null,
                                            },
                                            // Row 4: Banheiros Capacidade Água doce
                                            { id: 11, label: 'Banheiros', value: classified.bathrooms || '--', fieldName: 'bathrooms' },
                                            {
                                                id: 12,
                                                label: 'Capacidade Água Doce',
                                                value: classified.fresh_water_capacity ? `${classified.fresh_water_capacity}L` : '--',
                                                fieldName: 'fresh_water_capacity',
                                            },
                                            // Row 5: Horas de navegação
                                            {
                                                id: 13,
                                                label: 'Horas de Navegação',
                                                value: classified.sailing_time ? `${classified.sailing_time}h` : '--',
                                                fieldName: 'sailing_time',
                                            },
                                            // Engine
                                            {
                                                id: 16,
                                                label: 'Motores',
                                                value: classified.engine_quantity ? `${classified.engine_quantity}x` : '--',
                                                fieldName: 'engine_quantity',
                                            },
                                            {
                                                id: 17,
                                                label: 'Fabricante Motor',
                                                value: classified.engine_manufacturer || '--',
                                                fieldName: 'engine_manufacturer',
                                            },
                                            {
                                                id: 18,
                                                label: 'Modelo Motor',
                                                value: classified.engine_model || '--',
                                                fieldName: 'engine_model',
                                            },
                                            {
                                                id: 19,
                                                label: 'Potência (HP)',
                                                value: classified.engine_power_hp ? `${classified.engine_power_hp} HP` : '--',
                                                fieldName: 'engine_power_hp',
                                            },
                                            {
                                                id: 20,
                                                label: 'Combustível',
                                                value: classified.engine_fuel_type || '--',
                                                fieldName: 'engine_fuel_type',
                                            },
                                            // Generator
                                            {
                                                id: 21,
                                                label: 'Gerador',
                                                value: classified.generator_brand || '--',
                                                fieldName: 'generator_brand',
                                            },
                                            {
                                                id: 22,
                                                label: 'Modelo Gerador',
                                                value: classified.generator_model || '--',
                                                fieldName: 'generator_model',
                                            },
                                            {
                                                id: 23,
                                                label: 'Horas Gerador',
                                                value: classified.generator_hours ? `${classified.generator_hours}h` : '--',
                                                fieldName: 'generator_hours',
                                            },
                                        ]
                                            .filter((item) => {
                                                if (!item.fieldName) return true;
                                                return isFieldVisible(item.fieldName);
                                            })
                                            .filter((item) => {
                                                if (!item.fieldName) return true;
                                                const raw = (classified as any)[item.fieldName];
                                                return raw !== null && raw !== undefined && raw !== 0 && raw !== '';
                                            })
                                            .map((item) => (
                                                <div key={item.id} className={`flex flex-col space-y-1 ${item.hideOnMobile ? 'hidden md:flex' : ''}`}>
                                                    <div className="text-sm text-gray-600">{item.label}</div>
                                                    <div className="text-sm font-semibold sm:text-base">{item.value}</div>
                                                </div>
                                            ))}
                                    </div>
                                </div>

                                {/* Additional Information */}
                                {classified.conditions && (
                                    <div className="row-start-4 px-4 py-3 sm:px-8 sm:py-4 lg:px-12">
                                        <div className="flex flex-col">
                                            <div className="text-base font-semibold sm:text-lg">Informações Adicionais</div>
                                            <div className="text-sm leading-relaxed break-words whitespace-pre-wrap sm:text-base">
                                                {classified.conditions}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Description */}
                                <div className={`px-4 py-3 sm:px-8 sm:py-4 lg:px-12 ${classified.conditions ? 'row-start-5' : 'row-start-4'}`}>
                                    <div className="flex flex-col">
                                        <div className="text-base font-semibold sm:text-lg">Descrição</div>
                                        <div className="text-sm leading-relaxed break-words whitespace-pre-wrap sm:text-base">
                                            {classified.description}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Mobile Acessórios - hidden on desktop */}
                        {(() => {
                            // Handle different formats of optionals from backend
                            let selectedOptionals: string[] = [];

                            if (Array.isArray(classified.optionals)) {
                                selectedOptionals = classified.optionals;
                            } else if (typeof classified.optionals === 'object' && classified.optionals !== null) {
                                // If it's an object with category keys, flatten the values
                                selectedOptionals = Object.values(classified.optionals).flat() as string[];
                            }

                            if (selectedOptionals.length == 0) {
                                return <></>;
                            }

                            // Group selected optionals by category
                            const groupedOptionals = optionalCategories
                                .map((category) => {
                                    const categoryOptionals = category.optionals
                                        .filter((opt) => selectedOptionals.includes(opt.name))
                                        .map((opt) => opt.name);

                                    return categoryOptionals.length > 0 ? { category: category.name, optionals: categoryOptionals } : null;
                                })
                                .filter((item): item is { category: string; optionals: string[] } => item !== null);

                            if (groupedOptionals.length === 0) {
                                return null;
                            }

                            return (
                                <div className="px-4 py-3 md:hidden">
                                    <div className="flex flex-col">
                                        <h3 className="mb-4 text-lg font-medium text-black">Acessórios</h3>
                                        <div className="space-y-4">
                                            {groupedOptionals.map((group, index) => (
                                                <React.Fragment key={`${index}-outer-mobile`}>
                                                    <h4 className="mb-3 text-sm font-light text-gray-700">{group.category}</h4>
                                                    <div className="flex flex-col gap-2">
                                                        {group.optionals.map((value, optIndex) => (
                                                            <div key={optIndex} className="flex items-center gap-2">
                                                                <div className="flex size-[25px] flex-shrink-0 items-center justify-center">
                                                                    <img
                                                                        src={`/optionals-icon.svg`}
                                                                        alt="optionals icon"
                                                                        width={25}
                                                                        height={25}
                                                                        className="object-contain"
                                                                    />
                                                                </div>
                                                                <span className="text-sm text-gray-700">{value}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                        <div className={`col-span-1 rounded-xl bg-white p-4 sm:p-6 lg:col-span-1 lg:col-start-1 ${!hasOptionals ? 'md:hidden' : ''}`}>
                            <div className="flex flex-col space-y-6">
                                {/* Desktop Acessórios - hidden on mobile */}
                                <div className="hidden space-y-4 md:block">
                                    {(() => {
                                        // Handle different formats of optionals from backend
                                        let selectedOptionals: string[] = [];

                                        if (Array.isArray(classified.optionals)) {
                                            selectedOptionals = classified.optionals;
                                        } else if (typeof classified.optionals === 'object' && classified.optionals !== null) {
                                            // If it's an object with category keys, flatten the values
                                            selectedOptionals = Object.values(classified.optionals).flat() as string[];
                                        }

                                        if (selectedOptionals.length == 0) {
                                            return <></>;
                                        }

                                        // Group selected optionals by category
                                        const groupedOptionals = optionalCategories
                                            .map((category) => {
                                                const categoryOptionals = category.optionals
                                                    .filter((opt) => selectedOptionals.includes(opt.name))
                                                    .map((opt) => opt.name);

                                                return categoryOptionals.length > 0
                                                    ? { category: category.name, optionals: categoryOptionals }
                                                    : null;
                                            })
                                            .filter((item): item is { category: string; optionals: string[] } => item !== null);

                                        if (groupedOptionals.length === 0) {
                                            return null;
                                        }

                                        return (
                                            <>
                                                <h3 className="text-xl font-semibold text-black">Acessórios</h3>
                                                {groupedOptionals.map((group, index) => (
                                                    <React.Fragment key={`${index}-outer`}>
                                                        <h4 className="mb-3 text-base font-medium text-gray-700">{group.category}</h4>
                                                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                                            {group.optionals.map((value, optIndex) => (
                                                                <div key={optIndex} className="flex items-center space-x-2">
                                                                    <div className="flex size-8 items-center justify-center">
                                                                        <img
                                                                            src={`/optionals-icon.svg`}
                                                                            alt="optionals icon"
                                                                            width={80}
                                                                            height={80}
                                                                            className="object-contain"
                                                                        />
                                                                    </div>
                                                                    <span className="text-sm text-gray-700">{value}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </React.Fragment>
                                                ))}
                                            </>
                                        );
                                    })()}
                                </div>

                                {/* Mobile-only CTA group per Figma, below Opcionais */}
                                <div className="flex flex-col space-y-4 md:hidden">
                                    {/* Saiba mais: text only, same style as 'Sobre o vendedor' */}
                                    <div className="text-xl font-semibold text-black">Saiba mais</div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <Button variant="outline" className="h-10 w-full rounded-lg border-[#646A74] text-black" asChild>
                                            <a
                                                href="https://page.comprenautica.com.br/seguros?utm_source=CompreNautica&utm_medium=site&utm_campaign=button-cta-classified"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Cotar Seguro
                                            </a>
                                        </Button>
                                        <Button variant="outline" className="h-10 w-full rounded-lg border-[#646A74] text-black" asChild>
                                            <a
                                                href="https://page.comprenautica.com.br/consorciocomprenautica?utm_source=CompreNautica&utm_medium=site&utm_campaign=button-cta-classified"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Cotar Consórcio
                                            </a>
                                        </Button>
                                    </div>
                                    <Button className="relative flex h-10 w-full items-center justify-center rounded-lg bg-blue-800 text-white hover:bg-blue-800">
                                        <span>Resumo Inteligente</span>
                                        <svg
                                            width="23"
                                            height="23"
                                            viewBox="0 0 23 23"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="absolute right-3"
                                        >
                                            <path
                                                d="M12.5292 12.6128C12.3025 12.6128 12.1005 12.4698 12.0253 12.256L11.8375 11.7225C11.6498 11.1891 11.1458 10.8322 10.5803 10.8322H9.28128C8.71576 10.8322 8.21181 11.1891 8.02406 11.7225L7.8363 12.256C7.76104 12.4698 7.55905 12.6128 7.33238 12.6128C6.95993 12.6128 6.70178 12.2413 6.83171 11.8922L9.11408 5.76092C9.24107 5.41976 9.56675 5.19348 9.93078 5.19348C10.2948 5.19348 10.6205 5.41976 10.7475 5.76092L13.0298 11.8922C13.1598 12.2413 12.9016 12.6128 12.5292 12.6128ZM9.09563 8.67924C8.89161 9.2537 9.31763 9.85708 9.92724 9.85708C10.5359 9.85708 10.9618 9.25553 10.7596 8.68147C10.4833 7.89673 9.37405 7.89524 9.09563 8.67924Z"
                                                fill="white"
                                            />
                                            <path
                                                d="M14.2523 5.7246C14.2523 5.43127 14.4901 5.19348 14.7834 5.19348C15.0767 5.19348 15.3145 5.43127 15.3145 5.7246V12.0817C15.3145 12.375 15.0767 12.6128 14.7834 12.6128C14.4901 12.6128 14.2523 12.375 14.2523 12.0817V5.7246Z"
                                                fill="white"
                                            />
                                            <path
                                                d="M19.2158 0H3.39103C2.49167 0 1.62915 0.363476 0.993209 1.01047C0.357268 1.65746 0 2.53497 0 3.44995V14.9498C0 15.8648 0.357268 16.7423 0.993209 17.3893C1.62915 18.0363 2.49167 18.3997 3.39103 18.3997H12.2911C14.9778 18.3997 17.5515 19.4809 19.4322 21.3995L20.674 22.6662C20.7796 22.7728 20.9048 22.8571 21.0426 22.9143C21.1803 22.9715 21.3277 23.0006 21.4765 22.9997C21.6248 23.0036 21.7719 22.9721 21.906 22.9077C22.1125 22.8214 22.2892 22.6749 22.4139 22.4866C22.5386 22.2984 22.6057 22.0767 22.6068 21.8497V3.44995C22.6068 2.53497 22.2496 1.65746 21.6136 1.01047C20.9777 0.363476 20.1152 0 19.2158 0ZM20.3462 15.3774C20.3462 16.7302 18.7039 17.4001 17.7577 16.4333C17.6521 16.3267 17.5268 16.2424 17.3891 16.1851C17.2514 16.1279 17.1039 16.0989 16.9551 16.0998H3.39103C3.09124 16.0998 2.80374 15.9786 2.59175 15.763C2.37977 15.5473 2.26068 15.2548 2.26068 14.9498V3.44995C2.26068 3.14496 2.37977 2.85245 2.59175 2.63679C2.80374 2.42113 3.09124 2.29997 3.39103 2.29997H19.2158C19.5156 2.29997 19.8031 2.42113 20.0151 2.63679C20.2271 2.85245 20.3462 3.14496 20.3462 3.44995V15.3774Z"
                                                fill="white"
                                            />
                                        </svg>
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div className="col-span-1 rounded-xl bg-white p-4 sm:p-6 lg:col-span-1 lg:col-start-1">
                            <div className="flex flex-col space-y-4">
                                <div>
                                    <h3 className="text-xl font-semibold text-black">Sobre o vendedor</h3>
                                </div>

                                <div className="flex flex-row items-center space-x-4 sm:space-y-0">
                                    <div className="h-12 w-12 flex-shrink-0 rounded bg-gray-300"></div>
                                    <div className="flex min-w-0 flex-1 items-center gap-2 md:gap-4">
                                        <span className="text-sm font-semibold whitespace-nowrap text-gray-800 md:text-lg">
                                            {showFullPhone ? classified.contact_phone : hiddenPhone}
                                        </span>
                                        <Button
                                            onClick={handleClickPhone}
                                            className="h-10 flex-shrink-0 rounded-lg bg-blue-800 px-4 font-medium hover:bg-blue-700"
                                        >
                                            {showFullPhone ? 'Ocultar telefone' : 'Ver telefone'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Attachments Section */}
                        {classified.attachments && classified.attachments.length > 0 && (
                            <div className="col-span-1 rounded-xl bg-white p-4 sm:p-6 lg:col-span-1 lg:col-start-1">
                                <div className="mb-6 flex items-center justify-between">
                                    <h3 className="text-xl font-semibold text-black">Documents & Attachments</h3>
                                </div>

                                <div className="space-y-2">
                                    {classified.attachments.map((attachment) => (
                                        <div
                                            key={attachment.id}
                                            className="flex items-center rounded-lg border bg-gray-50 p-3 transition-colors hover:bg-gray-100"
                                        >
                                            <div className="mr-3 flex-shrink-0">
                                                <div className="flex h-10 w-10 items-center justify-center rounded-lg border bg-white">
                                                    <FileText className="h-5 w-5 text-gray-600" />
                                                </div>
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-medium text-gray-900">{attachment.name}</p>
                                                <p className="text-xs text-gray-500">
                                                    {getFileTypeLabel(attachment.mime_type ?? '')} • {formatFileSize(attachment.size ?? 0)}
                                                </p>
                                            </div>
                                            <div className="flex items-center">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="p-2"
                                                    onClick={() => window.open(attachment.source_original_url ?? attachment.original_url, '_blank')}
                                                >
                                                    <Download className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Similar Offers Section */}
                        <div className="col-span-1 rounded-xl bg-white p-4 sm:p-6 lg:col-span-2">
                            <div className="mb-6 flex items-center justify-between">
                                <h3 className="text-xl font-semibold text-black">Ofertas semelhantes</h3>
                            </div>

                            {similarOffers && similarOffers.length > 0 ? (
                                <ClassifiedsInlineBannerCarousel
                                    classifieds={similarOffers}
                                    banner={similarOffersBanner}
                                    favoriteStates={favoriteStates}
                                    onFavorite={handleFavorite}
                                    onClassifiedClick={(classifiedOffer) => router.visit(buildClassifiedPath(classifiedOffer.slug))}
                                    onBannerClick={handleBannerClick}
                                    onBannerVisible={trackBannerView}
                                    getPriceLabel={(classifiedOffer) =>
                                        getPublicPriceLabel({
                                            price: classifiedOffer.price,
                                            priceOnRequest: classifiedOffer.price_on_request,
                                        })
                                    }
                                    getConditionLabel={(classifiedOffer) => classifiedOffer.condition ?? ''}
                                />
                            ) : (
                                <p className="text-gray-500">Nenhuma oferta semelhante encontrada.</p>
                            )}
                        </div>

                        <div className="col-span-1 rounded-xl bg-white p-4 sm:p-6 lg:col-start-2 lg:row-start-1">
                            <div className="flex flex-col space-y-4 sm:space-y-6">
                                {/* Video call button - Mobile only, above price */}
                                <div className="md:hidden">
                                    <Button
                                        type="button"
                                        onClick={handleScrollToContact}
                                        className="relative flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-blue-800 px-6 text-sm font-medium hover:bg-blue-700 sm:h-12 sm:text-base"
                                    >
                                        <span className="text-center">Agendar videochamada</span>
                                        <svg
                                            width="32"
                                            height="29"
                                            viewBox="0 0 32 29"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="absolute right-3 flex-shrink-0"
                                        >
                                            <path
                                                d="M25.6 6.90751C25.6 6.29585 25.1042 5.8 24.4925 5.8H18.9C17.2984 5.8 16 4.50163 16 2.9C16 1.29837 14.7016 0 13.1 0H1.45C0.649188 0 0 0.649187 0 1.45C0 2.25081 0.649187 2.9 1.45 2.9H11.35C12.1508 2.9 12.8 3.54919 12.8 4.35C12.8 5.15081 12.1508 5.8 11.35 5.8H6.4C2.86538 5.8 0 8.66538 0 12.2V14.5C0 19.3049 3.89512 23.2 8.7 23.2H24.4925C25.1042 23.2 25.6 22.7042 25.6 22.0925C25.6 21.3821 26.2594 20.8554 26.9522 21.0124L28.8 21.431C30.4396 21.8025 32 20.556 32 18.8749V10.1251C32 8.44395 30.4396 7.19753 28.8 7.569L26.9522 7.98764C26.2594 8.14461 25.6 7.61791 25.6 6.90751ZM22.4 14.5C22.4 17.7033 19.8033 20.3 16.6 20.3H9C5.79675 20.3 3.2 17.7033 3.2 14.5C3.2 11.2967 5.79675 8.7 9 8.7H16.6C19.8033 8.7 22.4 11.2967 22.4 14.5ZM26.8465 10.9986C27.8474 10.7718 28.8 11.5327 28.8 12.5591V16.4409C28.8 17.4673 27.8474 18.2282 26.8465 18.0014C26.1175 17.8362 25.6 17.1884 25.6 16.441V12.5591C25.6 11.8116 26.1175 11.1638 26.8465 10.9986Z"
                                                fill="#FAFAFA"
                                            />
                                            <path
                                                d="M17.7504 26.0996H7.85039C7.04958 26.0996 6.40039 26.7488 6.40039 27.5496C6.40039 28.3504 7.04958 28.9996 7.85039 28.9996H17.7504C18.5512 28.9996 19.2004 28.3504 19.2004 27.5496C19.2004 26.7488 18.5512 26.0996 17.7504 26.0996Z"
                                                fill="#FAFAFA"
                                            />
                                        </svg>
                                    </Button>
                                </div>

                                {/* Price */}
                                <div>
                                    <h2 className="text-2xl font-bold text-black sm:text-3xl">
                                        {getPublicPriceLabel({
                                            price: classified.price,
                                            priceOnRequest: classified.price_on_request,
                                        })}
                                    </h2>
                                </div>

                                {/* Contact seller text */}
                                <div className="mb-1">
                                    <p className="text-sm text-black sm:text-base">Fale com um vendedor</p>
                                </div>

                                {/* Video call button */}
                                <div className="hidden md:block">
                                    <Button
                                        type="button"
                                        onClick={handleScrollToContact}
                                        className="relative flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-blue-800 px-6 text-sm font-medium hover:bg-blue-700 sm:h-12 sm:text-base md:w-auto md:justify-center"
                                    >
                                        <span className="text-center">Agendar videochamada</span>
                                        <svg
                                            width="32"
                                            height="29"
                                            viewBox="0 0 32 29"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="absolute right-3 flex-shrink-0 md:relative md:right-auto"
                                        >
                                            <path
                                                d="M25.6 6.90751C25.6 6.29585 25.1042 5.8 24.4925 5.8H18.9C17.2984 5.8 16 4.50163 16 2.9C16 1.29837 14.7016 0 13.1 0H1.45C0.649188 0 0 0.649187 0 1.45C0 2.25081 0.649187 2.9 1.45 2.9H11.35C12.1508 2.9 12.8 3.54919 12.8 4.35C12.8 5.15081 12.1508 5.8 11.35 5.8H6.4C2.86538 5.8 0 8.66538 0 12.2V14.5C0 19.3049 3.89512 23.2 8.7 23.2H24.4925C25.1042 23.2 25.6 22.7042 25.6 22.0925C25.6 21.3821 26.2594 20.8554 26.9522 21.0124L28.8 21.431C30.4396 21.8025 32 20.556 32 18.8749V10.1251C32 8.44395 30.4396 7.19753 28.8 7.569L26.9522 7.98764C26.2594 8.14461 25.6 7.61791 25.6 6.90751ZM22.4 14.5C22.4 17.7033 19.8033 20.3 16.6 20.3H9C5.79675 20.3 3.2 17.7033 3.2 14.5C3.2 11.2967 5.79675 8.7 9 8.7H16.6C19.8033 8.7 22.4 11.2967 22.4 14.5ZM26.8465 10.9986C27.8474 10.7718 28.8 11.5327 28.8 12.5591V16.4409C28.8 17.4673 27.8474 18.2282 26.8465 18.0014C26.1175 17.8362 25.6 17.1884 25.6 16.441V12.5591C25.6 11.8116 26.1175 11.1638 26.8465 10.9986Z"
                                                fill="#FAFAFA"
                                            />
                                            <path
                                                d="M17.7504 26.0996H7.85039C7.04958 26.0996 6.40039 26.7488 6.40039 27.5496C6.40039 28.3504 7.04958 28.9996 7.85039 28.9996H17.7504C18.5512 28.9996 19.2004 28.3504 19.2004 27.5496C19.2004 26.7488 18.5512 26.0996 17.7504 26.0996Z"
                                                fill="#FAFAFA"
                                            />
                                        </svg>
                                    </Button>
                                </div>

                                {/* Contact form */}
                                <form id="contact-form" onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                                    <Input
                                        placeholder="Nome *"
                                        value={formData.name}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                                        className={`h-10 w-full rounded-lg border-gray-300 text-sm sm:h-12 sm:text-base ${
                                            errors.name ? 'border-red-500' : ''
                                        }`}
                                        required
                                    />
                                    {errors.name && <div className="text-xs text-red-500">{errors.name}</div>}

                                    <Input
                                        type="email"
                                        placeholder="E-mail *"
                                        value={formData.email}
                                        onChange={(e) => {
                                            const emailValue = e.target.value;
                                            setFormData((prev) => ({ ...prev, email: emailValue }));
                                            // Clear email error when user starts typing
                                            if (errors.email) {
                                                setErrors((prev) => {
                                                    const newErrors = { ...prev };
                                                    delete newErrors.email;
                                                    return newErrors;
                                                });
                                            }
                                        }}
                                        onBlur={(e) => {
                                            // Validate email on blur
                                            if (e.target.value && !validateEmail(e.target.value)) {
                                                setErrors((prev) => ({
                                                    ...prev,
                                                    email: 'Por favor, insira um email válido',
                                                }));
                                            }
                                        }}
                                        className={`h-10 w-full rounded-lg border-gray-300 text-sm sm:h-12 sm:text-base ${
                                            errors.email ? 'border-red-500' : ''
                                        }`}
                                        required
                                    />
                                    {errors.email && <div className="text-xs text-red-500">{errors.email}</div>}

                                    <Input
                                        type="tel"
                                        placeholder="Telefone *"
                                        value={formData.phone}
                                        onChange={(e) => {
                                            const formatted = formatPhoneMask(e.target.value);
                                            setFormData((prev) => ({ ...prev, phone: formatted }));
                                            // Clear phone error when user starts typing
                                            if (errors.phone) {
                                                setErrors((prev) => {
                                                    const newErrors = { ...prev };
                                                    delete newErrors.phone;
                                                    return newErrors;
                                                });
                                            }
                                        }}
                                        className={`h-10 w-full rounded-lg border-gray-300 text-sm sm:h-12 sm:text-base ${
                                            errors.phone ? 'border-red-500' : ''
                                        }`}
                                        required
                                    />
                                    {errors.phone && <div className="text-xs text-red-500">{errors.phone}</div>}

                                    <Input
                                        type="text"
                                        placeholder="Assunto *"
                                        value={formData.subject}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, subject: e.target.value }))}
                                        className={`h-10 w-full rounded-lg border-gray-300 text-sm sm:h-12 sm:text-base ${
                                            errors.subject ? 'border-red-500' : ''
                                        }`}
                                        required
                                    />
                                    {errors.subject && <div className="text-xs text-red-500">{errors.subject}</div>}

                                    <Textarea
                                        placeholder="Olá, tenho interesse. Por favor, entre em contato."
                                        value={formData.message}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                                        className={`min-h-[100px] w-full resize-none rounded-lg border-gray-300 text-sm sm:min-h-[120px] sm:text-base ${
                                            errors.message ? 'border-red-500' : ''
                                        }`}
                                        required
                                    />
                                    {errors.message && <div className="text-xs text-red-500">{errors.message}</div>}

                                    {/* Success/Error messages */}
                                    {submitMessage && (
                                        <div
                                            className={`rounded-lg p-3 text-sm ${
                                                submitMessage.type === 'success'
                                                    ? 'border border-green-200 bg-green-100 text-green-800'
                                                    : 'border border-red-200 bg-red-100 text-red-800'
                                            }`}
                                        >
                                            {submitMessage.text}
                                        </div>
                                    )}

                                    {/* Consent checkbox */}
                                    <div className="flex items-start gap-3">
                                        <Checkbox
                                            id="consent-terms"
                                            checked={formData.consentAccepted}
                                            onCheckedChange={(checked) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    consentAccepted: checked === true,
                                                }))
                                            }
                                            className={`mt-1 ${errors.consentAccepted ? 'border-red-500' : ''} border border-primary`}
                                        />
                                        <label htmlFor="consent-terms" className="flex-1 cursor-pointer text-xs text-gray-500">
                                            Entendo que todas as informações inseridas aqui serão armazenadas no site, mas não serão publicamente
                                            visíveis nem pesquisáveis, exceto pelos Administradores do site e pelo autor do classificado. Entendo que
                                            posso ser contatado pelo administrador do site ou pelo autor do classificado.
                                        </label>
                                    </div>
                                    {errors.consentAccepted && <div className="text-xs text-red-500">{errors.consentAccepted}</div>}

                                    {/* reCAPTCHA info */}
                                    <div className="flex items-center space-x-3 rounded-lg border border-gray-300 bg-gray-50 p-3">
                                        <div className="flex h-6 w-6 items-center justify-center rounded bg-blue-500">
                                            <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <span className="text-sm text-gray-700">Protegido por reCAPTCHA</span>
                                        <div className="ml-auto">
                                            <div className="text-xs text-gray-400">Google</div>
                                        </div>
                                    </div>
                                    {errors.recaptcha_token && <div className="text-xs text-red-500">{errors.recaptcha_token}</div>}

                                    {/* Send message button */}
                                    <Button
                                        type="submit"
                                        disabled={processing}
                                        className="h-10 w-full rounded-lg bg-blue-800 text-sm font-medium hover:bg-blue-700 disabled:opacity-50 sm:h-12 sm:text-base"
                                    >
                                        {processing ? 'Enviando...' : 'Enviar mensagem'}
                                    </Button>
                                </form>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Classified Gallery Modal */}
                <ClassifiedGallery
                    images={galleryImages}
                    isOpen={isGalleryOpen}
                    currentIndex={currentImageIndex}
                    onClose={handleGalleryClose}
                    onNavigate={handleGalleryNavigate}
                />

                {/* Phone Verification Modal */}
                <Dialog
                    open={isPhoneModalOpen}
                    onOpenChange={(open) => {
                        setIsPhoneModalOpen(open);
                        if (!open) {
                            // Reset WhatsApp flow flag when modal closes
                            setIsWhatsAppFlow(false);
                        }
                    }}
                >
                    <DialogContent className="max-w-[393px] sm:max-w-lg">
                        <div className="relative w-full">
                            {/* Header with Back button */}
                            <div className="mb-6 flex items-center gap-4">
                                <DialogClose asChild>
                                    <Button variant="ghost" size="sm" className="h-auto p-2">
                                        <ArrowLeft className="h-4 w-4 text-[#677182]" />
                                    </Button>
                                </DialogClose>
                                <DialogClose asChild>
                                    <span className="cursor-pointer text-[15px] font-bold text-[#677182]">Voltar</span>
                                </DialogClose>
                            </div>

                            {/* Title - using DialogTitle for accessibility */}
                            <DialogTitle className="mb-2 text-center text-sm font-medium text-black uppercase">
                                {isWhatsAppFlow ? 'Abrir WhatsApp' : 'Veja o telefone'}
                            </DialogTitle>

                            {/* Subtitle - using DialogDescription for accessibility */}
                            <DialogDescription className="mb-6 text-center text-[15px] font-light text-black">
                                {isWhatsAppFlow ? 'Preencha os dados para abrir o WhatsApp' : 'Preencha os dados para liberar o telefone'}
                            </DialogDescription>

                            {/* Form */}
                            <form
                                onSubmit={(e) => {
                                    handlePhoneVerification(e);
                                }}
                                className="space-y-4"
                            >
                                {/* Name field */}
                                <div>
                                    <label className="mb-1 block text-sm text-black">Nome</label>
                                    <Input
                                        type="text"
                                        value={phoneVerificationForm.name}
                                        onChange={(e) => {
                                            setPhoneVerificationForm((prev) => ({ ...prev, name: e.target.value }));
                                            if (phoneVerificationErrors.name) {
                                                setPhoneVerificationErrors((prev) => ({ ...prev, name: '' }));
                                            }
                                        }}
                                        className={`h-[55.47px] w-full rounded-[10px] border border-[#646a74] text-sm ${
                                            phoneVerificationErrors.name ? 'border-red-500' : ''
                                        }`}
                                        required
                                    />
                                    {phoneVerificationErrors.name && <div className="mt-1 text-xs text-red-500">{phoneVerificationErrors.name}</div>}
                                </div>

                                {/* Email field */}
                                <div>
                                    <label className="mb-1 block text-sm text-black">E-mail</label>
                                    <Input
                                        type="email"
                                        value={phoneVerificationForm.email}
                                        onChange={(e) => {
                                            const emailValue = e.target.value;
                                            setPhoneVerificationForm((prev) => ({ ...prev, email: emailValue }));
                                            // Clear error when user starts typing
                                            if (phoneVerificationErrors.email) {
                                                setPhoneVerificationErrors((prev) => ({ ...prev, email: '' }));
                                            }
                                        }}
                                        onBlur={(e) => {
                                            const emailValue = e.target.value;
                                            if (emailValue && !validateEmail(emailValue)) {
                                                setPhoneVerificationErrors((prev) => ({
                                                    ...prev,
                                                    email: 'Por favor, insira um email válido',
                                                }));
                                            }
                                        }}
                                        className={`h-[55.47px] w-full rounded-[10px] border border-[#646a74] text-sm ${
                                            phoneVerificationErrors.email ? 'border-red-500' : ''
                                        }`}
                                        required
                                    />
                                    {phoneVerificationErrors.email && (
                                        <div className="mt-1 text-xs text-red-500">{phoneVerificationErrors.email}</div>
                                    )}
                                </div>

                                {/* Phone field */}
                                <div>
                                    <label className="mb-1 block text-sm text-black">Telefone</label>
                                    <Input
                                        type="tel"
                                        value={phoneVerificationForm.phone}
                                        onChange={(e) => {
                                            const formatted = formatPhoneMask(e.target.value);
                                            setPhoneVerificationForm((prev) => ({ ...prev, phone: formatted }));
                                            // Clear error when user starts typing
                                            if (phoneVerificationErrors.phone) {
                                                setPhoneVerificationErrors((prev) => ({ ...prev, phone: '' }));
                                            }
                                        }}
                                        placeholder="(00) 00000-0000"
                                        maxLength={15}
                                        className={`h-[55.47px] w-full rounded-[10px] border border-[#646a74] text-sm ${
                                            phoneVerificationErrors.phone ? 'border-red-500' : ''
                                        }`}
                                        required
                                    />
                                    {phoneVerificationErrors.phone && (
                                        <div className="mt-1 text-xs text-red-500">{phoneVerificationErrors.phone}</div>
                                    )}
                                </div>

                                {/* Terms checkbox */}
                                <div className="flex items-start gap-3">
                                    <Checkbox
                                        id="phone-terms"
                                        checked={phoneVerificationForm.termsAccepted}
                                        onCheckedChange={(checked) =>
                                            setPhoneVerificationForm((prev) => ({
                                                ...prev,
                                                termsAccepted: checked === true,
                                            }))
                                        }
                                        className="mt-1 border border-primary bg-white"
                                    />
                                    <label htmlFor="phone-terms" className="flex-1 cursor-pointer text-justify text-xs font-light text-black">
                                        Entendo que todas as informações inseridas aqui serão armazenadas no site, mas não serão publicamente visíveis
                                        nem pesquisáveis, exceto pelos Administradores do site e pelo autor do classificado. Entendo que posso ser
                                        contatado pelo administrador do site e pelo autor do classificado.
                                    </label>
                                </div>
                                {phoneVerificationErrors.termsAccepted && (
                                    <div className="text-xs text-red-500">{phoneVerificationErrors.termsAccepted}</div>
                                )}

                                {/* reCAPTCHA info */}
                                <div className="flex items-center space-x-3 rounded-[10px] border border-[#646a74] bg-[#dce1ea] p-3">
                                    <div className="flex h-6 w-6 items-center justify-center rounded bg-blue-500">
                                        <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <span className="flex-1 text-center text-sm text-black">Não sou um robô</span>
                                    <div className="ml-auto">
                                        <div className="text-xs text-gray-400">Google</div>
                                    </div>
                                </div>
                                {phoneVerificationErrors.recaptcha && <div className="text-xs text-red-500">{phoneVerificationErrors.recaptcha}</div>}

                                {/* General error */}
                                {phoneVerificationErrors.general && (
                                    <div className="rounded-lg border border-red-200 bg-red-100 p-3 text-sm text-red-800">
                                        {phoneVerificationErrors.general}
                                    </div>
                                )}

                                {/* Visualizar/Abrir WhatsApp button */}
                                <Button
                                    type="submit"
                                    disabled={phoneVerificationProcessing}
                                    className="h-[53.239px] w-full rounded-[10px] bg-[#0027B2] text-[20px] font-medium text-white hover:bg-[#0020a0] disabled:opacity-50"
                                >
                                    {phoneVerificationProcessing ? 'Verificando...' : isWhatsAppFlow ? 'Abrir WhatsApp' : 'Visualizar'}
                                </Button>
                            </form>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Mobile bottom action bar: Whatsapp + Contatar */}
                {!isGalleryOpen && (
                    <div className="fixed inset-x-0 bottom-0 z-50 border-t border-gray-200 bg-white/90 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-white/70 md:hidden">
                        <div className="flex gap-3">
                            <Button
                                className="h-12 flex-1 rounded-lg bg-[#00BB08] text-sm font-medium text-white hover:bg-[#00a507]"
                                onClick={() => {
                                    setIsWhatsAppFlow(true);
                                    setIsPhoneModalOpen(true);
                                }}
                            >
                                Whatsapp
                            </Button>
                            <Button
                                className="h-12 flex-1 rounded-lg bg-[#0027B2] text-sm font-medium text-white hover:bg-[#0020a0]"
                                onClick={handleScrollToContact}
                            >
                                Contatar
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </FrontendLayout>
    );
}

export default function ClassifiedDetail(props: ClassifiedDetailProps) {
    return (
        <RecaptchaProvider>
            <ClassifiedDetailPage {...props} />
        </RecaptchaProvider>
    );
}
